import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ImageIcon, Upload, Trash2, Save, Loader2, CheckCircle, 
  Image, Palette, Info, Type, Moon, Sun, Monitor, Eye, CreditCard
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme, GOOGLE_FONTS } from '@/context/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrandingPage = () => {
  const { mode, setMode, toggleMode, isDark, fonts, setHeadingFont, setBodyFont } = useTheme();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState({
    name: '',
    logo_url: null,
    primary_color: '#14b8a6',
    secondary_color: '#0d9488',
    card_primary_color: '#14b8a6',
    card_secondary_color: '#0d9488'
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API}/clinic/branding`);
      const data = response.data;
      setBranding({
        ...data,
        card_primary_color: data.card_primary_color || data.primary_color || '#14b8a6',
        card_secondary_color: data.card_secondary_color || data.secondary_color || '#0d9488'
      });
      if (data.logo_url) {
        setPreviewUrl(data.logo_url);
      }
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use PNG, JPG, WebP or SVG.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('File is too large. Maximum size is 2MB.');
      return;
    }

    setSelectedFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/clinic/branding/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const newLogoUrl = response.data.logo_url;
      setBranding(prev => ({ ...prev, logo_url: newLogoUrl }));
      setPreviewUrl(newLogoUrl);
      setSelectedFile(null);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      toast.success('Logo uploaded successfully!');
      window.dispatchEvent(new CustomEvent('clinic-branding-updated'));
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!branding.logo_url) return;
    if (!window.confirm('Are you sure you want to delete the logo?')) return;

    setUploading(true);
    try {
      await axios.delete(`${API}/clinic/branding/logo`);
      setBranding(prev => ({ ...prev, logo_url: null }));
      setPreviewUrl(null);
      setSelectedFile(null);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      toast.success('Logo deleted');
      window.dispatchEvent(new CustomEvent('clinic-branding-updated'));
    } catch (error) {
      console.error('Failed to delete logo:', error);
      toast.error('Failed to delete logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveColors = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clinic/info`, {
        settings: {
          primary_color: branding.primary_color,
          secondary_color: branding.secondary_color,
          card_primary_color: branding.card_primary_color,
          card_secondary_color: branding.card_secondary_color
        }
      });
      toast.success('Colors saved successfully!');
      window.dispatchEvent(new CustomEvent('clinic-branding-updated'));
    } catch (error) {
      console.error('Failed to save colors:', error);
      toast.error('Failed to save colors');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCardColors = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clinic/info`, {
        settings: {
          card_primary_color: branding.card_primary_color,
          card_secondary_color: branding.card_secondary_color
        }
      });
      toast.success('Membership card colors saved!');
      window.dispatchEvent(new CustomEvent('clinic-branding-updated'));
    } catch (error) {
      console.error('Failed to save card colors:', error);
      toast.error('Failed to save card colors');
    } finally {
      setSaving(false);
    }
  };

  const resetCardColorsToDefault = () => {
    setBranding(prev => ({
      ...prev,
      card_primary_color: prev.primary_color,
      card_secondary_color: prev.secondary_color
    }));
    toast.info('Card colors reset to match brand colors');
  };

  // Color presets for membership cards
  const cardColorPresets = [
    { name: 'Teal (Default)', primary: '#0d9488', secondary: '#14b8a6' },
    { name: 'Blue Professional', primary: '#1e40af', secondary: '#3b82f6' },
    { name: 'Purple Premium', primary: '#6b21a8', secondary: '#a855f7' },
    { name: 'Green Health', primary: '#166534', secondary: '#22c55e' },
    { name: 'Navy Classic', primary: '#1e3a5f', secondary: '#3b5998' },
    { name: 'Rose Gold', primary: '#9f1239', secondary: '#f43f5e' },
    { name: 'Golden Luxury', primary: '#92400e', secondary: '#f59e0b' },
    { name: 'Slate Modern', primary: '#334155', secondary: '#64748b' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: branding.primary_color }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'var(--font-heading)' }}>
          Clinic Branding
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Customize your clinic's appearance with logo, colors, fonts, and theme
        </p>
      </div>

      <Tabs defaultValue="logo" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-flex">
          <TabsTrigger value="logo" className="gap-2">
            <ImageIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Logo</span>
          </TabsTrigger>
          <TabsTrigger value="colors" className="gap-2">
            <Palette className="h-4 w-4" />
            <span className="hidden sm:inline">Colors</span>
          </TabsTrigger>
          <TabsTrigger value="card" className="gap-2">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Member Card</span>
          </TabsTrigger>
          <TabsTrigger value="fonts" className="gap-2">
            <Type className="h-4 w-4" />
            <span className="hidden sm:inline">Fonts</span>
          </TabsTrigger>
          <TabsTrigger value="theme" className="gap-2">
            <Moon className="h-4 w-4" />
            <span className="hidden sm:inline">Theme</span>
          </TabsTrigger>
        </TabsList>

        {/* Logo Tab */}
        <TabsContent value="logo">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" style={{ color: branding.primary_color }} />
                Clinic Logo
              </CardTitle>
              <CardDescription>
                Upload your clinic logo to display across all portals
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col md:flex-row gap-6">
                {/* Preview Area */}
                <div className="flex-shrink-0">
                  <Label className="text-sm font-medium mb-2 block">Preview</Label>
                  <div className="relative w-40 h-40 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-800 flex items-center justify-center overflow-hidden">
                    {previewUrl ? (
                      <img src={previewUrl} alt="Logo preview" className="w-full h-full object-contain p-2" />
                    ) : (
                      <div className="text-center p-4">
                        <Image className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                        <span className="text-sm text-gray-500">No logo</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Upload Controls */}
                <div className="flex-1 space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Upload new logo</Label>
                    <div className="flex flex-wrap gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} className="gap-2">
                        <Upload className="h-4 w-4" />
                        Select file
                      </Button>
                      
                      {selectedFile && (
                        <Button onClick={handleUpload} disabled={uploading} style={{ backgroundColor: branding.primary_color }}>
                          {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : <><Save className="h-4 w-4 mr-2" />Save logo</>}
                        </Button>
                      )}
                      
                      {branding.logo_url && !selectedFile && (
                        <Button variant="outline" onClick={handleDeleteLogo} disabled={uploading} className="text-red-600 hover:bg-red-50">
                          <Trash2 className="h-4 w-4 mr-2" />Delete
                        </Button>
                      )}
                    </div>
                  </div>

                  {selectedFile && (
                    <div className="flex items-center gap-2 text-sm p-3 rounded-lg" style={{ backgroundColor: `${branding.primary_color}15`, color: branding.primary_color }}>
                      <CheckCircle className="h-4 w-4" />
                      <span>File selected: {selectedFile.name}</span>
                    </div>
                  )}

                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Recommendations:</strong>
                      <ul className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        <li>• Maximum size: 2MB</li>
                        <li>• Formats: PNG, JPG, WebP, SVG</li>
                        <li>• Recommended: 200x200px square with transparent background</li>
                      </ul>
                    </AlertDescription>
                  </Alert>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Colors Tab */}
        <TabsContent value="colors">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" style={{ color: branding.primary_color }} />
                Brand Colors
              </CardTitle>
              <CardDescription>
                Customize your clinic's color scheme across all portals
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Primary Color */}
                <div className="space-y-3">
                  <Label className="font-medium">Primary Color</Label>
                  <p className="text-sm text-gray-500">Used for buttons, links, and key UI elements</p>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <input
                        type="color"
                        value={branding.primary_color}
                        onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                        className="w-16 h-16 rounded-xl cursor-pointer border-2 border-gray-200"
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        value={branding.primary_color}
                        onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                        className="font-mono uppercase"
                        maxLength={7}
                      />
                    </div>
                  </div>
                </div>

                {/* Secondary Color */}
                <div className="space-y-3">
                  <Label className="font-medium">Secondary Color</Label>
                  <p className="text-sm text-gray-500">Used for gradients, accents, and hover states</p>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <input
                        type="color"
                        value={branding.secondary_color}
                        onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                        className="w-16 h-16 rounded-xl cursor-pointer border-2 border-gray-200"
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        value={branding.secondary_color}
                        onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                        className="font-mono uppercase"
                        maxLength={7}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="border-t pt-6">
                <Label className="font-medium mb-4 block">Preview</Label>
                <div className="flex flex-wrap gap-4">
                  <Button style={{ backgroundColor: branding.primary_color }}>Primary Button</Button>
                  <Button style={{ backgroundColor: branding.secondary_color }}>Secondary Button</Button>
                  <Button variant="outline" style={{ borderColor: branding.primary_color, color: branding.primary_color }}>
                    Outline Button
                  </Button>
                  <div className="px-4 py-2 rounded-lg text-white" style={{ background: `linear-gradient(135deg, ${branding.primary_color}, ${branding.secondary_color})` }}>
                    Gradient
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveColors} disabled={saving} style={{ backgroundColor: branding.primary_color }}>
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save Colors</>}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Membership Card Tab */}
        <TabsContent value="card">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" style={{ color: branding.card_primary_color }} />
                Membership Card Design
              </CardTitle>
              <CardDescription>
                Customize the colors for patient membership cards. These can be different from your brand colors.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Color Presets */}
              <div className="space-y-3">
                <Label className="font-medium">Quick Presets</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {cardColorPresets.map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => setBranding(prev => ({
                        ...prev,
                        card_primary_color: preset.primary,
                        card_secondary_color: preset.secondary
                      }))}
                      className={`p-3 rounded-lg border-2 transition-all hover:scale-105 ${
                        branding.card_primary_color === preset.primary && branding.card_secondary_color === preset.secondary
                          ? 'border-gray-800 dark:border-white shadow-lg'
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div 
                        className="h-8 rounded-md mb-2"
                        style={{ background: `linear-gradient(135deg, ${preset.primary}, ${preset.secondary})` }}
                      />
                      <span className="text-xs font-medium">{preset.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Card Primary Color */}
                <div className="space-y-3">
                  <Label className="font-medium">Card Primary Color</Label>
                  <p className="text-sm text-gray-500">Main gradient start color</p>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <input
                        type="color"
                        value={branding.card_primary_color}
                        onChange={(e) => setBranding(prev => ({ ...prev, card_primary_color: e.target.value }))}
                        className="w-16 h-16 rounded-xl cursor-pointer border-2 border-gray-200"
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        value={branding.card_primary_color}
                        onChange={(e) => setBranding(prev => ({ ...prev, card_primary_color: e.target.value }))}
                        className="font-mono uppercase"
                        maxLength={7}
                      />
                    </div>
                  </div>
                </div>

                {/* Card Secondary Color */}
                <div className="space-y-3">
                  <Label className="font-medium">Card Secondary Color</Label>
                  <p className="text-sm text-gray-500">Gradient accent color</p>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <input
                        type="color"
                        value={branding.card_secondary_color}
                        onChange={(e) => setBranding(prev => ({ ...prev, card_secondary_color: e.target.value }))}
                        className="w-16 h-16 rounded-xl cursor-pointer border-2 border-gray-200"
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        value={branding.card_secondary_color}
                        onChange={(e) => setBranding(prev => ({ ...prev, card_secondary_color: e.target.value }))}
                        className="font-mono uppercase"
                        maxLength={7}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Live Card Preview */}
              <div className="border-t pt-6">
                <Label className="font-medium mb-4 block flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Live Card Preview
                </Label>
                <div className="flex justify-center">
                  <div 
                    style={{
                      width: '380px',
                      height: '240px',
                      borderRadius: '14px',
                      background: `linear-gradient(145deg, ${branding.card_primary_color} 0%, ${branding.card_secondary_color} 50%, ${branding.card_primary_color} 100%)`,
                      boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                  >
                    {/* Pattern overlay */}
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 20px, transparent 20px, transparent 40px)',
                      pointerEvents: 'none'
                    }} />
                    
                    {/* Card Content */}
                    <div style={{ 
                      position: 'relative', 
                      zIndex: 10, 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      justifyContent: 'space-between', 
                      padding: '14px',
                      color: 'white',
                      fontFamily: 'Arial, sans-serif'
                    }}>
                      {/* Header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {previewUrl ? (
                            <div style={{ width: '40px', height: '40px', backgroundColor: 'white', borderRadius: '6px', padding: '3px' }}>
                              <img src={previewUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </div>
                          ) : (
                            <div style={{ width: '40px', height: '40px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontWeight: 700, fontSize: '20px' }}>{branding.name?.charAt(0) || 'C'}</span>
                            </div>
                          )}
                          <div>
                            <p style={{ fontWeight: 700, fontSize: '15px', margin: 0 }}>{branding.name || 'Your Clinic Name'}</p>
                            <p style={{ fontSize: '11px', opacity: 0.9, fontWeight: 600, margin: 0, marginTop: '3px', letterSpacing: '0.5px' }}>MEMBER CARD</p>
                          </div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '5px', borderRadius: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                          <div style={{ width: '72px', height: '72px', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="50" height="50" viewBox="0 0 100 100" fill="none">
                              <rect x="10" y="10" width="20" height="20" fill="#333"/>
                              <rect x="40" y="10" width="20" height="20" fill="#333"/>
                              <rect x="70" y="10" width="20" height="20" fill="#333"/>
                              <rect x="10" y="40" width="20" height="20" fill="#333"/>
                              <rect x="40" y="40" width="20" height="20" fill="#333"/>
                              <rect x="70" y="40" width="20" height="20" fill="#333"/>
                              <rect x="10" y="70" width="20" height="20" fill="#333"/>
                              <rect x="40" y="70" width="20" height="20" fill="#333"/>
                              <rect x="70" y="70" width="20" height="20" fill="#333"/>
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Member Info */}
                      <div style={{ paddingTop: '10px', paddingBottom: '10px' }}>
                        <p style={{ fontSize: '11px', opacity: 0.9, textTransform: 'uppercase', marginBottom: '4px', fontWeight: 500, letterSpacing: '0.5px' }}>Member Name</p>
                        <p style={{ fontWeight: 700, fontSize: '22px', letterSpacing: '0.5px', margin: 0 }}>JOHN DOE</p>
                        <div style={{ display: 'flex', gap: '24px', marginTop: '12px' }}>
                          <div>
                            <p style={{ fontSize: '11px', opacity: 0.9, textTransform: 'uppercase', fontWeight: 500, marginBottom: '3px', letterSpacing: '0.5px' }}>ID</p>
                            <p style={{ fontFamily: 'Courier New, monospace', fontWeight: 700, fontSize: '14px', margin: 0 }}>PAT-2402-0001-7</p>
                          </div>
                          <div>
                            <p style={{ fontSize: '11px', opacity: 0.9, textTransform: 'uppercase', fontWeight: 500, marginBottom: '3px', letterSpacing: '0.5px' }}>Plan</p>
                            <p style={{ fontWeight: 700, fontSize: '14px', margin: 0 }}>Premium Membership</p>
                          </div>
                        </div>
                      </div>

                      {/* Footer */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingTop: '8px', borderTop: '2px solid rgba(255,255,255,0.35)' }}>
                        <div>
                          <p style={{ fontSize: '11px', opacity: 0.9, textTransform: 'uppercase', marginBottom: '3px', letterSpacing: '0.5px' }}>Valid Until</p>
                          <p style={{ fontWeight: 700, fontSize: '14px', margin: 0 }}>12/31/2026</p>
                        </div>
                        <div style={{ backgroundColor: 'rgba(255,255,255,0.25)', borderRadius: '6px', padding: '6px 10px' }}>
                          <span style={{ fontWeight: 700, fontSize: '13px' }}>📞 (555) 123-4567</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <Button onClick={handleSaveCardColors} disabled={saving} style={{ backgroundColor: branding.card_primary_color }}>
                  {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save Card Colors</>}
                </Button>
                <Button variant="outline" onClick={resetCardColorsToDefault}>
                  Reset to Brand Colors
                </Button>
              </div>

              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Card colors are applied to all membership cards generated for your patients. Changes will reflect on new card downloads.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fonts Tab */}
        <TabsContent value="fonts">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Type className="h-5 w-5" style={{ color: branding.primary_color }} />
                Typography
              </CardTitle>
              <CardDescription>
                Choose custom fonts for headings and body text (powered by Google Fonts)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Heading Font */}
                <div className="space-y-3">
                  <Label className="font-medium">Heading Font</Label>
                  <p className="text-sm text-gray-500">Used for titles and headers</p>
                  <Select value={fonts.heading} onValueChange={setHeadingFont}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select font" />
                    </SelectTrigger>
                    <SelectContent>
                      {GOOGLE_FONTS.map(font => (
                        <SelectItem key={font.value} value={font.value} style={{ fontFamily: `"${font.value}", ${font.category}` }}>
                          {font.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-2xl font-bold" style={{ fontFamily: `"${fonts.heading}", sans-serif` }}>
                      The Quick Brown Fox
                    </p>
                  </div>
                </div>

                {/* Body Font */}
                <div className="space-y-3">
                  <Label className="font-medium">Body Font</Label>
                  <p className="text-sm text-gray-500">Used for paragraphs and general text</p>
                  <Select value={fonts.body} onValueChange={setBodyFont}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select font" />
                    </SelectTrigger>
                    <SelectContent>
                      {GOOGLE_FONTS.map(font => (
                        <SelectItem key={font.value} value={font.value} style={{ fontFamily: `"${font.value}", ${font.category}` }}>
                          {font.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-base" style={{ fontFamily: `"${fonts.body}", sans-serif` }}>
                      Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    </p>
                  </div>
                </div>
              </div>

              {/* Font Preview Card */}
              <div className="border-t pt-6">
                <Label className="font-medium mb-4 block flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Live Preview
                </Label>
                <Card className="overflow-hidden">
                  <div className="p-6" style={{ background: `linear-gradient(135deg, ${branding.primary_color}15, ${branding.secondary_color}08)` }}>
                    <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: `"${fonts.heading}", sans-serif`, color: branding.primary_color }}>
                      Welcome to Your Clinic
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400" style={{ fontFamily: `"${fonts.body}", sans-serif` }}>
                      This is how your text will appear throughout the application. The heading font is used for titles, while the body font is used for regular content like this paragraph.
                    </p>
                    <div className="mt-4 flex gap-2">
                      <Button size="sm" style={{ backgroundColor: branding.primary_color, fontFamily: `"${fonts.body}", sans-serif` }}>
                        Get Started
                      </Button>
                      <Button size="sm" variant="outline" style={{ borderColor: branding.primary_color, color: branding.primary_color, fontFamily: `"${fonts.body}", sans-serif` }}>
                        Learn More
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>

              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Font changes are applied instantly and saved to your browser. They will persist across sessions.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Theme Tab */}
        <TabsContent value="theme">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {isDark ? <Moon className="h-5 w-5" style={{ color: branding.primary_color }} /> : <Sun className="h-5 w-5" style={{ color: branding.primary_color }} />}
                Theme Mode
              </CardTitle>
              <CardDescription>
                Choose between light and dark mode for the interface
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Theme Options */}
              <div className="grid grid-cols-3 gap-4">
                {/* Light Mode */}
                <button
                  onClick={() => setMode('light')}
                  className={`p-4 rounded-xl border-2 transition-all ${mode === 'light' ? 'border-2 shadow-lg' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                  style={mode === 'light' ? { borderColor: branding.primary_color } : {}}
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-16 h-12 bg-white border rounded-lg flex items-center justify-center shadow-sm">
                      <Sun className="h-6 w-6 text-amber-500" />
                    </div>
                    <span className="font-medium text-sm">Light</span>
                    {mode === 'light' && (
                      <CheckCircle className="h-5 w-5" style={{ color: branding.primary_color }} />
                    )}
                  </div>
                </button>

                {/* Dark Mode */}
                <button
                  onClick={() => setMode('dark')}
                  className={`p-4 rounded-xl border-2 transition-all ${mode === 'dark' ? 'border-2 shadow-lg' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                  style={mode === 'dark' ? { borderColor: branding.primary_color } : {}}
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-16 h-12 bg-slate-800 border border-slate-700 rounded-lg flex items-center justify-center">
                      <Moon className="h-6 w-6 text-slate-300" />
                    </div>
                    <span className="font-medium text-sm">Dark</span>
                    {mode === 'dark' && (
                      <CheckCircle className="h-5 w-5" style={{ color: branding.primary_color }} />
                    )}
                  </div>
                </button>

                {/* System (Auto) */}
                <button
                  onClick={() => {
                    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                    setMode(prefersDark ? 'dark' : 'light');
                  }}
                  className="p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 transition-all"
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-16 h-12 bg-gradient-to-r from-white to-slate-800 border rounded-lg flex items-center justify-center">
                      <Monitor className="h-6 w-6 text-gray-600" />
                    </div>
                    <span className="font-medium text-sm">System</span>
                  </div>
                </button>
              </div>

              {/* Quick Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                <div className="flex items-center gap-3">
                  {isDark ? <Moon className="h-5 w-5 text-blue-400" /> : <Sun className="h-5 w-5 text-amber-500" />}
                  <div>
                    <p className="font-medium">Quick Toggle</p>
                    <p className="text-sm text-gray-500">Switch between light and dark mode</p>
                  </div>
                </div>
                <Switch
                  checked={isDark}
                  onCheckedChange={toggleMode}
                />
              </div>

              {/* Preview */}
              <div className="border-t pt-6">
                <Label className="font-medium mb-4 block flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Theme Preview
                </Label>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Light Preview */}
                  <div className="p-4 bg-white border rounded-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: branding.primary_color }}>
                        C
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Light Mode</p>
                        <p className="text-xs text-gray-500">Clean and bright</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>

                  {/* Dark Preview */}
                  <div className="p-4 bg-slate-800 border border-slate-700 rounded-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: branding.primary_color }}>
                        C
                      </div>
                      <div>
                        <p className="font-semibold text-white">Dark Mode</p>
                        <p className="text-xs text-slate-400">Easy on the eyes</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-3 bg-slate-700 rounded w-3/4"></div>
                      <div className="h-3 bg-slate-700 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              </div>

              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Theme preference is saved to your browser and will persist across sessions.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BrandingPage;
